<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatPercent } from '../../helpers/operationsChartHelper';

const props = defineProps({
  presence: {
    type: Object,
    default: () => ({ daysInRange: 0, agents: [] }),
  },
  agents: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

// Connected and working are different questions and share one grid: the same shape read twice,
// so an hour that is bright on presence and dark on activity stands out as idle time.
const MODES = ['presence', 'activity'];
const mode = ref(MODES[0]);

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const HOUR_LABEL_STRIDE = 3;
const SECONDS_IN_HOUR = 3600;

// Radix blue 1..9 as a ramp: an hour nobody was connected stays at the flat background, so the
// shape of the shift reads from across the room.
const SHADES = [
  'bg-n-alpha-1',
  'bg-n-blue-3',
  'bg-n-blue-4',
  'bg-n-blue-5',
  'bg-n-blue-6',
  'bg-n-blue-7',
  'bg-n-blue-8',
  'bg-n-blue-9',
];

const shadeFor = ratio => {
  if (!ratio) return SHADES[0];
  return SHADES[
    Math.min(SHADES.length - 1, 1 + Math.floor(ratio * (SHADES.length - 1)))
  ];
};

const minutes = seconds => Math.round(seconds / 60);

const agentName = id => {
  const agent = props.agents.find(record => record.id === id);
  return (
    agent?.available_name ||
    agent?.name ||
    t('OPERATIONS_REPORTS.PRESENCE.REMOVED_AGENT')
  );
};

const agentThumbnail = id =>
  props.agents.find(record => record.id === id)?.thumbnail || '';

// Averaged over the days the agent actually worked, not over the whole range: a Monday to Friday
// shift measured against seven days would read as two thirds of a shift.
const rows = computed(() =>
  props.presence.agents
    .map(agent => {
      const byHour = new Map(agent.hours.map(hour => [hour.hour, hour]));
      const days = agent.activeDays || 1;

      return {
        id: agent.id,
        name: agentName(agent.id),
        thumbnail: agentThumbnail(agent.id),
        activeDays: agent.activeDays,
        dailyHours:
          (agent.onlineSeconds + agent.busySeconds) / days / SECONDS_IN_HOUR,
        dailyMessages: (agent.messages || 0) / days,
        cells: HOURS.map(hour => {
          const slot = byHour.get(hour);
          const online = (slot?.onlineSeconds || 0) / days;
          const busy = (slot?.busySeconds || 0) / days;
          const present = online + busy;
          const messages = (slot?.messages || 0) / days;

          return {
            hour,
            present,
            messages,
            // A second device opens a second session, so an hour can carry more than an hour of
            // overlapping presence. The shade tops out; the tooltip still shows what was measured.
            presenceRatio: Math.min(present / SECONDS_IN_HOUR, 1),
            tip: [
              `${agentName(agent.id)} · ${String(hour).padStart(2, '0')}h`,
              t('OPERATIONS_REPORTS.PRESENCE.TIP_PRESENT', {
                minutes: minutes(present),
              }),
              t('OPERATIONS_REPORTS.PRESENCE.TIP_SPLIT', {
                available: minutes(online),
                busy: minutes(busy),
              }),
              t('OPERATIONS_REPORTS.PRESENCE.TIP_MESSAGES', {
                messages: formatPercent(messages, locale.value),
              }),
              t('OPERATIONS_REPORTS.PRESENCE.TIP_DAYS', {
                days: slot?.days || 0,
                activeDays: agent.activeDays,
              }),
            ].join('\n'),
          };
        }),
      };
    })
    .sort((first, second) =>
      mode.value === 'activity'
        ? second.dailyMessages - first.dailyMessages
        : second.dailyHours - first.dailyHours
    )
);

// Activity has no natural ceiling the way an hour of presence does, so its scale is the busiest
// cell in the grid.
const peakMessages = computed(() =>
  Math.max(
    ...rows.value.flatMap(row => row.cells.map(cell => cell.messages)),
    0.001
  )
);

const cellRatio = cell =>
  mode.value === 'activity'
    ? Math.min(cell.messages / peakMessages.value, 1)
    : cell.presenceRatio;

// How many people are around at each hour - the coverage question, as opposed to the individual
// schedule question the rows below answer.
const teamRow = computed(() => {
  const totals = HOURS.map(hour => ({
    agentsPresent: rows.value.reduce(
      (sum, row) => sum + row.cells[hour].present / SECONDS_IN_HOUR,
      0
    ),
    messages: rows.value.reduce(
      (sum, row) => sum + row.cells[hour].messages,
      0
    ),
  }));
  const peakPresence = Math.max(
    ...totals.map(total => total.agentsPresent),
    0.001
  );
  const peakActivity = Math.max(...totals.map(total => total.messages), 0.001);

  return totals.map((total, hour) => ({
    hour,
    agentsPresent: total.agentsPresent,
    messages: total.messages,
    ratio:
      mode.value === 'activity'
        ? total.messages / peakActivity
        : total.agentsPresent / peakPresence,
    tip: [
      `${String(hour).padStart(2, '0')}h`,
      t('OPERATIONS_REPORTS.PRESENCE.TIP_COVERAGE', {
        agents: formatPercent(total.agentsPresent, locale.value),
      }),
      t('OPERATIONS_REPORTS.PRESENCE.TIP_TEAM_MESSAGES', {
        messages: formatPercent(total.messages, locale.value),
      }),
    ].join('\n'),
  }));
});

const peakHour = computed(() => {
  const metric = row =>
    mode.value === 'activity' ? row.messages : row.agentsPresent;
  const best = [...teamRow.value].sort(
    (first, second) => metric(second) - metric(first)
  )[0];
  return best && metric(best) ? best.hour : null;
});
</script>

<template>
  <div class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('OPERATIONS_REPORTS.PRESENCE.TITLE') }}
        </h3>
        <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
          {{ t('OPERATIONS_REPORTS.PRESENCE.SUBTITLE') }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex items-center gap-1">
          <Button
            v-for="option in MODES"
            :key="option"
            :label="
              t(`OPERATIONS_REPORTS.PRESENCE.MODE_${option.toUpperCase()}`)
            "
            :variant="mode === option ? 'faded' : 'ghost'"
            color="slate"
            size="xs"
            @click="mode = option"
          />
        </div>
        <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
          <span>{{ t('OPERATIONS_REPORTS.PRESENCE.LESS') }}</span>
          <i
            v-for="shade in SHADES.slice(1)"
            :key="shade"
            class="w-2.5 h-2.5 rounded-sm"
            :class="shade"
          />
          <span>{{ t('OPERATIONS_REPORTS.PRESENCE.MORE') }}</span>
        </div>
      </div>
    </div>

    <p
      v-if="!rows.length"
      class="py-10 mb-0 text-sm text-center text-n-slate-10"
    >
      {{ t('OPERATIONS_REPORTS.PRESENCE.EMPTY') }}
    </p>

    <div v-else class="mt-4 overflow-x-auto">
      <div class="min-w-[42rem]">
        <div class="grid items-center gap-x-2 grid-cols-[11rem_1fr_5.5rem]">
          <span />
          <div class="grid gap-px grid-cols-[repeat(24,minmax(0,1fr))]">
            <span
              v-for="hour in HOURS"
              :key="`label-${hour}`"
              class="text-[10px] text-center text-n-slate-10 tabular-nums"
            >
              {{ hour % HOUR_LABEL_STRIDE === 0 ? hour : '' }}
            </span>
          </div>
          <span />
        </div>

        <div
          class="grid items-center py-2 mt-1 border-b gap-x-2 grid-cols-[11rem_1fr_5.5rem] border-n-weak"
        >
          <span class="text-xs font-medium truncate text-n-slate-11">
            {{ t('OPERATIONS_REPORTS.PRESENCE.TEAM') }}
          </span>
          <div class="grid gap-px grid-cols-[repeat(24,minmax(0,1fr))]">
            <span
              v-for="cell in teamRow"
              :key="`team-${cell.hour}`"
              class="h-4 rounded-sm"
              :class="shadeFor(cell.ratio)"
              :data-tip="cell.tip"
            />
          </div>
          <span class="text-xs text-right text-n-slate-10">
            {{
              peakHour === null
                ? '—'
                : t('OPERATIONS_REPORTS.PRESENCE.PEAK', { hour: peakHour })
            }}
          </span>
        </div>

        <div
          v-for="row in rows"
          :key="row.id"
          class="grid items-center py-1.5 gap-x-2 grid-cols-[11rem_1fr_5.5rem]"
        >
          <div class="flex items-center min-w-0 gap-2">
            <Avatar
              :src="row.thumbnail"
              :name="row.name"
              :size="22"
              rounded-full
            />
            <span class="text-xs truncate text-n-slate-12">{{ row.name }}</span>
          </div>
          <div class="grid gap-px grid-cols-[repeat(24,minmax(0,1fr))]">
            <span
              v-for="cell in row.cells"
              :key="`${row.id}-${cell.hour}`"
              class="h-4 rounded-sm"
              :class="shadeFor(cellRatio(cell))"
              :data-tip="cell.tip"
            />
          </div>
          <span
            class="text-xs text-right text-n-slate-10 tabular-nums"
            :data-tip="
              t('OPERATIONS_REPORTS.PRESENCE.TIP_ACTIVE_DAYS', {
                days: row.activeDays,
                total: presence.daysInRange,
              })
            "
          >
            {{
              mode === 'activity'
                ? t('OPERATIONS_REPORTS.PRESENCE.DAILY_MESSAGES', {
                    messages: formatPercent(row.dailyMessages, locale),
                  })
                : t('OPERATIONS_REPORTS.PRESENCE.DAILY_HOURS', {
                    hours: formatPercent(row.dailyHours, locale),
                  })
            }}
          </span>
        </div>
      </div>

      <p
        v-if="mode === 'activity' && presence.unattributedMessages"
        class="pt-3 mt-3 mb-0 text-xs border-t text-n-slate-10 border-n-weak"
      >
        {{
          t('OPERATIONS_REPORTS.PRESENCE.UNATTRIBUTED', {
            count: presence.unattributedMessages,
          })
        }}
      </p>
    </div>
  </div>
</template>
